import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BookOpen, ArrowLeft, ArrowRight, Code, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../services/api';

function SectionContent() {
  const { programId, sectionOrder } = useParams();
  const [searchParams] = useSearchParams();
  const enrollmentId = searchParams.get('enrollmentId');
  const navigate = useNavigate();
  
  const [content, setContent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchContent();
  }, [programId, sectionOrder]);

  const fetchContent = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/section-content/program/${programId}/section/${sectionOrder}`);
      setContent(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const goToNextPage = () => {
    if (currentPage < content.totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const goToAssessment = () => {
    navigate(`/learning/${enrollmentId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3 mb-6">
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-700">{error}</p>
            </div>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="w-full py-3 px-4 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const currentPageData = content.pages.find(p => p.pageNumber === currentPage);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div style={{marginTop: "73px"}} className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-8 px-4 sticky top-0 z-10 shadow-lg">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6" />
              <h2 className="text-2xl font-bold">{content.sectionTitle}</h2>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-all border border-white/30 inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-300">Page {currentPage} of {content.totalPages}</span>
              <span className="text-slate-300">{Math.round((currentPage / content.totalPages) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-green-400 to-green-500 h-full rounded-full transition-all duration-500"
                style={{ width: `${(currentPage / content.totalPages) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Content Card */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-6">{currentPageData.title}</h1>
          
          {/* HTML Content */}
          <div 
            className="prose prose-slate max-w-none mb-6"
            dangerouslySetInnerHTML={{ __html: currentPageData.content }}
          />

          {/* Code Snippets */}
          {currentPageData.codeSnippets && currentPageData.codeSnippets.length > 0 && (
            <div className="space-y-6 mt-8">
              {currentPageData.codeSnippets.map((snippet, index) => (
                <div key={index} className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                  <div className="bg-slate-800 text-white px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Code className="w-4 h-4" />
                      <span className="font-semibold">{snippet.description}</span>
                    </div>
                    <span className="text-xs bg-slate-700 px-2 py-1 rounded uppercase">{snippet.language}</span>
                  </div>
                  <pre className="bg-slate-900 text-slate-100 p-4 overflow-x-auto">
                    <code className={`language-${snippet.language}`}>
{snippet.code}
                    </code>
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className="px-6 py-3 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Previous
          </button>

          {currentPage === content.totalPages ? (
            <button 
              onClick={goToAssessment}
              className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2"
            >
              <CheckCircle className="w-5 h-5" />
              Complete & Go to Assessment
            </button>
          ) : (
            <button 
              onClick={goToNextPage}
              className="px-6 py-3 bg-slate-900 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2"
            >
              Next
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Page Navigation Dots */}
        <div className="text-center">
          <div className="inline-flex gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2">
            {Array.from({ length: content.totalPages }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                  pageNum === currentPage
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                }`}
                onClick={() => {
                  setCurrentPage(pageNum);
                  window.scrollTo(0, 0);
                }}
              >
                {pageNum}
              </button>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .prose {
          line-height: 1.8;
          color: #334155;
        }
        .prose h3 {
          color: #0f172a;
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        .prose h4 {
          color: #1e293b;
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }
        .prose ul, .prose ol {
          margin-bottom: 1.5rem;
          padding-left: 1.5rem;
        }
        .prose li {
          margin-bottom: 0.5rem;
        }
        .prose strong {
          color: #0f172a;
          font-weight: 600;
        }
        .prose code {
          background-color: #f1f5f9;
          padding: 2px 6px;
          border-radius: 4px;
          font-family: 'Courier New', monospace;
          color: #e11d48;
          font-size: 0.9em;
        }
        .prose table {
          width: 100%;
          margin: 1.5rem 0;
          border-collapse: collapse;
          border: 1px solid #e2e8f0;
        }
        .prose th, .prose td {
          border: 1px solid #e2e8f0;
          padding: 0.75rem;
          text-align: left;
        }
        .prose th {
          background-color: #f8fafc;
          font-weight: 600;
          color: #0f172a;
        }
        .prose p {
          margin-bottom: 1rem;
        }
        .prose a {
          color: #0f172a;
          font-weight: 500;
          text-decoration: underline;
        }
        .prose a:hover {
          color: #475569;
        }
      `}</style>
    </div>
  );
}

export default SectionContent;
